import React, { useEffect, useRef, useState } from 'react';
import cs from 'classnames';

import { loadData, JoinType, postTea } from '../setting';
import { getConfigList } from '@service/api';
import { initHomeData } from '@redux/actions/home';

import { Message } from '@byted/serial-components-i18n';
import Header from '@components/mobile-header';
import ArrowBtn from '@components/arrow-btn';
import Footer from '@components/footer';
import ScrollContainer from '@components/scroll-container';

import { useTranslation } from '@ies/intl-react-plugin';
import { useHeaderTheme, useHome } from '@utils/hooks';
import { useAccount } from '@containers/account';
import { useDispatch } from 'react-redux';

import { getWinScale } from '@utils/util';
import { Tea } from '@utils/tea';

import { renderI18nText } from '@utils/text';
import { LangCode } from '@common/lang';
import HomeApp from './download';

import './index.less';
import { HeaderTheme } from '@constants/enum';

const WRITER_ITEM_MAX_HEIGHT = 727;

const Home = () => {
    const [writerShow, setWriterShow] = useState<any>({});
    const [writerMask, setWriterMask] = useState<any>({});

    const bodyRef = useRef<HTMLDivElement>(null);

    const { i18n } = useTranslation();
    const dispatch = useDispatch();

    const { getAccountPopup, handleWrite } = useAccount(true);

    const { writer_notes, advantages, book_trends, serverInited } = useHome();

    const headerInfo = useHeaderTheme({
        ref: bodyRef,
        getModules: () => Array.from(bodyRef.current!.querySelectorAll('section, footer')),
        pageThemes: [
            HeaderTheme.Black,
            HeaderTheme.Black,
            HeaderTheme.Black,
            HeaderTheme.White,
            HeaderTheme.Black,
            HeaderTheme.White,
            HeaderTheme.Black,
            HeaderTheme.White,
        ],
        pageBgs: ['transparent', '#d8a462', '#eee', '#1a1a1a', '#eee', '#1a1a1a', '#eee', '#000'],
        getHeaderHeight: () => getWinScale() * 44,
        onShow: postTea,
    });

    useEffect(() => {
        if (!serverInited) {
            getConfig();
        }
    }, []);

    const getConfig = async () => {
        try {
            const resp = await getConfigList({ language: i18n.language });
            if (!resp.code) {
                dispatch(initHomeData(resp.data));
            }
        } catch {
            Message.error('error');
        }
    };

    useEffect(() => {
        if (writer_notes && writer_notes.length <= 0) {
            return;
        }
        setTimeout(() => {
            setWriterMaskShow();
        });
    }, [writer_notes]);

    const setWriterMaskShow = () => {
        const target = document.querySelectorAll<HTMLDivElement>('.home-writer-item');
        const scale = getWinScale();
        let writerMaskShow = {};
        Array.from(target).forEach((item, i) => {
            if (item.clientHeight * scale > WRITER_ITEM_MAX_HEIGHT) {
                writerMaskShow = {
                    ...writerMaskShow,
                    [i]: true,
                };
            } else {
                writerMaskShow = {
                    ...writerMaskShow,
                    [i]: false,
                };
            }
        });
        setWriterMask(writerMaskShow);
    };

    const handleSetWriterShow = (i: number, show: boolean) => {
        let height;
        if (show) {
            const target = document.querySelectorAll<HTMLDivElement>('.home-writer-item-container')[i];
            height = `${target?.firstElementChild?.clientHeight}px`;
        }
        setWriterShow({
            ...writerShow,
            [i]: height ? { height } : null,
        });
    };

    const onJoin = (type: number) => {
        handleWrite();
        Tea('website_join', { join_page: type });
    };

    const bookLeft = book_trends?.slice(0, book_trends.length / 2);
    const bookRight = book_trends?.slice(book_trends.length / 2);
    const lang = i18n.language as LangCode;

    return (
        <div className={cs('home', { [lang]: lang !== undefined })} ref={bodyRef}>
            <Header {...headerInfo} />
            <section className="home-index">
                <div className="home-index-top">
                    {i18n.t('web_slogan')}&nbsp;
                    {i18n.t('web_slogan1')}
                    <br />
                    {i18n.t('web_slogan2')}
                </div>
                <div className="home-index-divider" />
                <div className="home-index-bottom">
                    <p>{i18n.t('web_slogan_note')}</p>
                    <ArrowBtn text={i18n.t('web_firstpage_button')} onClick={() => onJoin(JoinType.Banner)} />
                </div>
            </section>
            <section className="home-advantage">
                <h1>{i18n.t('web_workwithus')}</h1>
                <ScrollContainer>
                    {advantages!.map((item, index) => (
                        <div className="home-advantage-item" key={index}>
                            <p>{item.title}</p>
                            <div dangerouslySetInnerHTML={{ __html: item.content }} />
                        </div>
                    ))}
                </ScrollContainer>
            </section>
            <section className="home-story">
                <h1>{i18n.t('web_longstory')}</h1>
                <p className="home-story-tip">
                    {i18n.t('web_longstory_note1')}
                    <br />
                    {i18n.t('web_longstory_note2')}&nbsp;
                    {i18n.t('web_longstory_note3')}&nbsp;
                    {renderI18nText(i18n.t('web_longstory_note4'))}&nbsp;
                </p>
                <div className="home-story-list">
                    <div className="home-story-item">
                        <p>
                            <span>{i18n.t('web_longstory_exclusive')}</span>
                        </p>
                        <div>
                            {i18n.t('web_longstory_exclusive_note1')}&nbsp;
                            <b>{i18n.t('web_longstory_exclusive_note2')}</b>
                        </div>
                    </div>
                    <div className="home-story-item">
                        <p>
                            <span>{i18n.t('web_longstory_nonexclusive')}</span>
                        </p>
                        <div>
                            {i18n.t('web_longstory_nonexclusive1')}&nbsp;
                            <b>{i18n.t('web_longstory_nonexclusive2')}</b>
                        </div>
                    </div>
                </div>
                <ArrowBtn text={i18n.t('web_longstory_button')} onClick={() => onJoin(JoinType.Banner)} />
            </section>
            <section className="home-writer">
                <h1>{i18n.t('web_fromwriter')}</h1>
                <div className="home-writer-list">
                    <ScrollContainer>
                        {writer_notes!.map((item, index) => {
                            const defaultShowMask = writerMask[index];
                            const showHeight = writerShow[index];
                            return (
                                <div className="home-writer-item-wrapper" key={index}>
                                    <div className="home-writer-item-container" style={showHeight}>
                                        <div className="home-writer-item">
                                            <div className="home-writer-item-img">
                                                <img src={item.picture_url} alt="" />
                                                {/* <p>{item.writer_name}</p> */}
                                            </div>
                                            <div className="home-writer-item-text">
                                                {item.intro}
                                                <div className="home-writer-item-text-bg">
                                                    <p>{item.writer_name}</p>
                                                </div>
                                            </div>
                                            <div
                                                className="home-writer-item-detail"
                                                dangerouslySetInnerHTML={{ __html: item.note }}
                                            />
                                            {(showHeight || !defaultShowMask) && (
                                                <>
                                                    <img className="home-writer-item-sign" src={item.signature_url} />
                                                    {defaultShowMask && (
                                                        <span
                                                            className="home-writer-item-packup"
                                                            onClick={() => handleSetWriterShow(index, false)}
                                                        />
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </div>
                                    {!showHeight && defaultShowMask && (
                                        <div className="home-writer-item-mask">
                                            <span onClick={() => handleSetWriterShow(index, true)}>
                                                {i18n.t('common_more')}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </ScrollContainer>
                </div>
            </section>
            <section className="home-trend">
                <h1>{i18n.t('web_trend')}</h1>
                <div className="home-trend-list">
                    <div className="home-trend-left">
                        {bookLeft?.map((item, index) => (
                            <div className="home-trend-item" key={index}>
                                <img src={item.cover_url} />
                                <span>{item.book_name}</span>
                            </div>
                        ))}
                    </div>
                    <div className="home-trend-right">
                        {bookRight?.map((item, index) => (
                            <div className="home-trend-item" key={index}>
                                <img src={item.cover_url} />
                                <span>{item.book_name}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
            <HomeApp />
            <Footer />
            {getAccountPopup()}
        </div>
    );
};
Home.loadData = loadData;

export default Home;
